import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useImageGallery } from '../../hooks/useImageGallery';
import { ImageModal } from './image-modal';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  isUserMessage?: boolean;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = React.memo(({
  content,
  className = '',
  isUserMessage = false
}) => {
  const { isOpen, images, currentIndex, openGallery, closeGallery, setCurrentIndex } = useImageGallery();

  // Preprocess content to unescape markdown images
  const preprocessContent = (text: string): string => {
    // Try multiple patterns to unescape markdown images
    let result = text;

    // Pattern 1: !\[alt](url) -> ![alt](url) (both brackets escaped)
    result = result.replace(/!\\\[([^\]]*)\\\]\(([^)]+)\)/g, '![$1]($2)');

    // Pattern 2: Handle cases where only opening bracket is escaped
    result = result.replace(/!\\\[([^\]]*)\]\(([^)]+)\)/g, '![$1]($2)');

    // Pattern 3: Handle cases where only closing bracket is escaped  
    result = result.replace(/!\[([^\]]*)\\\]\(([^)]+)\)/g, '![$1]($2)');

    return result;
  };

  const processedContent = preprocessContent(content);

  // Extract all images from content for gallery (with priority order)
  const extractImages = (text: string): string[] => {
    const matches = new Set<string>(); // Use Set to avoid duplicates
    let match;

    // Priority 1: Images with prefix like "Ảnh: ![alt](url)" - HIGHEST PRIORITY
    const prefixedImageRegex = /(?:Ảnh|Image|Hình|Photo|Picture):\s*!\[.*?\]\((.*?)\)/gi;
    while ((match = prefixedImageRegex.exec(text)) !== null) {
      matches.add(match[1]);
    }

    // Priority 2: Standard markdown images ![alt](url) - HIGH PRIORITY
    const standardImageRegex = /!\[.*?\]\((.*?)\)/g;
    while ((match = standardImageRegex.exec(text)) !== null) {
      matches.add(match[1]);
    }

    // Priority 3: Direct image URLs (common image extensions) - LOW PRIORITY
    const directImageRegex = /(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s]*)?)/gi;
    while ((match = directImageRegex.exec(text)) !== null) {
      matches.add(match[1]);
    }

    return Array.from(matches);
  };

  const allImages = extractImages(processedContent);

  // Get appropriate text color based on message type
  const getTextColorClass = () => {
    if (isUserMessage) {
      return 'text-primary-foreground'; // White text for user messages
    }
    return 'text-gray-800 dark:text-gray-200'; // Default text color for assistant
  };

  const handleImageClick = (src: string) => {
    const imageIndex = allImages.indexOf(src);
    if (allImages.length === 0) {
      return;
    }
    openGallery(allImages, imageIndex);
  };

  // Helper function to render image with text
  const renderImageWithText = (imageUrl: string, alt: string = "Content") => {
    // Don't render if imageUrl is empty or invalid
    if (!imageUrl || imageUrl.trim() === '') {
      return null;
    }
    
    return (
      <div className="my-2">
        <img 
          src={imageUrl} 
          alt={alt} 
          className="max-h-[300px] w-auto object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          loading="lazy"
          onClick={() => handleImageClick(imageUrl)}
        />
      </div>
    );
  };

  // Helper function to render video with text
  const renderVideoWithText = (videoUrl: string, remainingText: string, extraClassName: string = "", label: string = "") => {
    // Extract video ID from YouTube URL
    const getYouTubeVideoId = (url: string): string | null => {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
      return match ? match[1] : null;
    };

    const videoId = getYouTubeVideoId(videoUrl);
    if (!videoId) return null;

      return (
        <div className="relative w-full max-w-[500px] sm:max-w-[500px] !mb-4">
          {label && (
            <span className="block mb-2 text-sm text-muted-foreground">
              {label}
            </span>
          )}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title="YouTube video"
              className="absolute top-0 left-0 w-full h-full rounded-lg"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      );
  };

  // Helper function to render external link with text
  const renderExternalLinkWithText = (linkUrl: string, remainingText: string, extraClassName: string = "", label: string = "") => {
    // Extract domain from URL for display
    const getDomain = (url: string): string => {
      try {
        const domain = new URL(url).hostname;
        return domain.replace('www.', '');
      } catch {
        return url;
      }
    };

    const domain = getDomain(linkUrl);

    return (
      <div className="my-2">
        {label && (
          <p className={`${getTextColorClass()} leading-relaxed font-medium`}>
            {label}
          </p>
        )}
        {remainingText && (
          <p className={`${getTextColorClass()} leading-relaxed`}>
            {remainingText}
          </p>
        )}
        <div className="w-fit max-w-full line-clamp-1 bg-gray-50/80 dark:bg-gray-800/80 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
          <a 
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-md font-medium truncate !mb-0">{domain}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate !mb-0">{linkUrl}</p>
            </div>
            <div className="flex-shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={`markdown-content ${className}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          skipHtml={false}
          components={{
            // Tables
            table: ({ children }) => (
              <div className="overflow-x-auto my-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-gray-50 dark:bg-gray-700">
                {children}
              </thead>
            ),
            tbody: ({ children }) => (
              <tbody className="bg-white dark:bg-gray-800">
                {children}
              </tbody>
            ),
            tr: ({ children }) => (
              <tr className="border-b border-gray-200 dark:border-gray-600">
                {children}
              </tr>
            ),
            th: ({ children }) => (
              <th className={`border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-semibold ${isUserMessage ? 'text-primary-foreground' : 'text-gray-900 dark:text-gray-100'}`}>
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className={`border border-gray-300 dark:border-gray-600 px-3 py-2 ${getTextColorClass()}`}>
                {children}
              </td>
            ),
            // Headings
            h1: ({ children }) => (
              <h1 className={`text-xl font-bold mb-2 ${isUserMessage ? 'text-primary-foreground' : 'text-gray-900 dark:text-gray-100'}`}>
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className={`text-lg font-semibold mb-2 ${isUserMessage ? 'text-primary-foreground' : 'text-gray-900 dark:text-gray-100'}`}>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className={`text-base font-medium ${isUserMessage ? 'text-primary-foreground' : 'text-gray-900 dark:text-gray-100'}`}>
                {children}
              </h3>
            ),
            // Paragraphs with image detection
            p: ({ children, ...props }) => {
              // Convert children to string for pattern matching
              const getTextContent = (node: any): string => {
                if (typeof node === 'string') return node;
                if (typeof node === 'number') return String(node);
                if (Array.isArray(node)) return node.map(getTextContent).join('');
                if (node && typeof node === 'object' && node.props && node.props.children) {
                  return getTextContent(node.props.children);
                }
                return '';
              };

              const text = getTextContent(children);

              // Only handle prefixed images here - let ReactMarkdown handle standard markdown images
              const prefixedImageMatch = text.match(/(?:Ảnh|Image|Hình|Photo|Picture):\s*!\[.*?\]\((.*?)\)/i);
              if (prefixedImageMatch) {
                const imageUrl = prefixedImageMatch[1];
                if (imageUrl && imageUrl.trim() !== '') {
                  const remainingText = text.replace(/(?:Ảnh|Image|Hình|Photo|Picture):\s*!\[.*?\]\(.*?\)/i, '').trim();
                  return renderImageWithText(imageUrl, remainingText);
                }
              }

              // Handle prefixed videos like "Video: [text](url)"
              const prefixedVideoMatch = text.match(/((?:Video|Vidéo|Phim):)\s*\[.*?\]\((.*?)\)/i);
              if (prefixedVideoMatch) {
                const label = prefixedVideoMatch[1]; // "Video:"
                const videoUrl = prefixedVideoMatch[2];
                if (videoUrl && videoUrl.trim() !== '') {
                  const remainingText = text.replace(/(?:Video|Vidéo|Phim):\s*\[.*?\]\(.*?\)/i, '').trim();
                  return renderVideoWithText(videoUrl, remainingText, '', label);
                }
              }

              // Handle direct image URLs (not in markdown format)
              const directImageMatch = text.match(/(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s]*)?)/i);
              if (directImageMatch) {
                const imageUrl = directImageMatch[1];
                if (imageUrl && imageUrl.trim() !== '') {
                  const remainingText = text.replace(/(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|ico)(?:\?[^\s]*)?)/i, '').trim();
                  return renderImageWithText(imageUrl, remainingText);
                }
              }

              // Handle direct YouTube URLs
              const directVideoMatch = text.match(/(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+))/i);
              if (directVideoMatch) {
                const videoUrl = directVideoMatch[1];
                if (videoUrl && videoUrl.trim() !== '') {
                  const remainingText = text.replace(/(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+))/i, '').trim();
                  return renderVideoWithText(videoUrl, remainingText);
                }
              }

              // Handle prefixed external links like "Link: [text](url)"
              const prefixedLinkMatch = text.match(/((?:Link|Lien|Liên kết):)\s*\[.*?\]\((.*?)\)/i);
              if (prefixedLinkMatch) {
                const label = prefixedLinkMatch[1]; // "Link:"
                const linkUrl = prefixedLinkMatch[2];
                const remainingText = text.replace(/(?:Link|Lien|Liên kết):\s*\[.*?\]\(.*?\)/i, '').trim();
                return renderExternalLinkWithText(linkUrl, remainingText, '', label);
              }

              // Handle Vietnamese prefixed links like "Link sản phẩm" followed by URL
              const vietnameseLinkMatch = text.match(/((?:Link sản phẩm|Ảnh sản phẩm|Video sản phẩm|Link|Ảnh|Video)):\s*(https?:\/\/[^\s]+)/i);
              if (vietnameseLinkMatch) {
                const label = vietnameseLinkMatch[1]; // "Link sản phẩm", "Ảnh sản phẩm", etc.
                const linkUrl = vietnameseLinkMatch[2];
                const remainingText = text.replace(/(?:Link sản phẩm|Ảnh sản phẩm|Video sản phẩm|Link|Ảnh|Video):\s*(https?:\/\/[^\s]+)/i, '').trim();
                return renderExternalLinkWithText(linkUrl, remainingText, '', label);
              }

              // Handle direct external URLs (not images or videos)
              const directLinkMatch = text.match(/(https?:\/\/[^\s]+)/i);
              if (directLinkMatch) {
                const linkUrl = directLinkMatch[1];
                // Skip if it's an image or video URL
                if (!linkUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?[^\s]*)?$/i) && 
                    !linkUrl.match(/(?:youtube\.com|youtu\.be)/i)) {
                  const remainingText = text.replace(/(https?:\/\/[^\s]+)/i, '').trim();
                  return renderExternalLinkWithText(linkUrl, remainingText);
                }
              }

              // Default paragraph rendering
              return (
                <p className={`${getTextColorClass()} leading-relaxed !mb-2 [li_&]:!mb-0 [ol_&]:!mb-0 [ul_&]:!mb-0`}>
                  {children}
                </p>
              );
            },
            // Lists
            ul: ({ children }) => (
              <ul className={`space-y-1 ${getTextColorClass()}`}>
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className={`space-y-1 ${getTextColorClass()}`}>
                {children}
              </ol>
            ),
            li: ({ children }) => {
              // Convert children to string for pattern matching
              const getTextContent = (node: any): string => {
                if (typeof node === 'string') return node;
                if (typeof node === 'number') return String(node);
                if (Array.isArray(node)) return node.map(getTextContent).join('');
                if (node && typeof node === 'object' && node.props && node.props.children) {
                  return getTextContent(node.props.children);
                }
                return '';
              };
              
              const text = getTextContent(children);

              // Handle prefixed videos in list items like "Video: [text](url)"
              const prefixedVideoMatch = text.match(/((?:Video|Vidéo|Phim):)\s*\[.*?\]\((.*?)\)/i);
              if (prefixedVideoMatch) {
                const label = prefixedVideoMatch[1]; // "Video:"
                const videoUrl = prefixedVideoMatch[2];
                const remainingText = text.replace(/(?:Video|Vidéo|Phim):\s*\[.*?\]\(.*?\)/i, '').trim();
                return (
                  <li className={`${getTextColorClass()} leading-relaxed`}>
                    {renderVideoWithText(videoUrl, remainingText, '', label)}
                  </li>
                );
              }

              // Handle direct YouTube URLs in list items
              const directVideoMatch = text.match(/(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+))/i);
              if (directVideoMatch) {
                const videoUrl = directVideoMatch[1];
                const remainingText = text.replace(/(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+))/i, '').trim();
                return (
                  <li className={`${getTextColorClass()} leading-relaxed`}>
                    {renderVideoWithText(videoUrl, remainingText)}
                  </li>
                );
              }

              // Handle prefixed external links in list items like "Link: [text](url)"
              const prefixedLinkMatch = text.match(/((?:Link|Lien|Liên kết):)\s*\[.*?\]\((.*?)\)/i);
              if (prefixedLinkMatch) {
                const label = prefixedLinkMatch[1]; // "Link:"
                const linkUrl = prefixedLinkMatch[2];
                const remainingText = text.replace(/(?:Link|Lien|Liên kết):\s*\[.*?\]\(.*?\)/i, '').trim();
                return (
                  <li className={`${getTextColorClass()} leading-relaxed`}>
                    {renderExternalLinkWithText(linkUrl, remainingText, '', label)}
                  </li>
                );
              }

              // Handle Vietnamese prefixed links in list items like "Link sản phẩm" followed by URL
              const vietnameseLinkMatch = text.match(/((?:Link sản phẩm|Ảnh sản phẩm|Video sản phẩm|Link|Ảnh|Video)):\s*(https?:\/\/[^\s]+)/i);
              if (vietnameseLinkMatch) {
                const label = vietnameseLinkMatch[1]; // "Link sản phẩm", "Ảnh sản phẩm", etc.
                const linkUrl = vietnameseLinkMatch[2];
                const remainingText = text.replace(/(?:Link sản phẩm|Ảnh sản phẩm|Video sản phẩm|Link|Ảnh|Video):\s*(https?:\/\/[^\s]+)/i, '').trim();
                return (
                  <li className={`${getTextColorClass()} leading-relaxed`}>
                    {renderExternalLinkWithText(linkUrl, remainingText, '', label)}
                  </li>
                );
              }

              // Handle direct external URLs in list items (not images or videos)
              const directLinkMatch = text.match(/(https?:\/\/[^\s]+)/i);
              if (directLinkMatch) {
                const linkUrl = directLinkMatch[1];
                // Skip if it's an image or video URL
                if (!linkUrl.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?[^\s]*)?$/i) && 
                    !linkUrl.match(/(?:youtube\.com|youtu\.be)/i)) {
                  const remainingText = text.replace(/(https?:\/\/[^\s]+)/i, '').trim();
                return (
                  <li className={`${getTextColorClass()} leading-relaxed`}>
                    {renderExternalLinkWithText(linkUrl, remainingText)}
                  </li>
                );
                }
              }

              // Default list item rendering
              return (
                <li className={`${getTextColorClass()} leading-relaxed`}>
                  {children}
                </li>
              );
            },
            // Text formatting
            strong: ({ children }) => (
              <strong className={`font-semibold ${isUserMessage ? 'text-primary-foreground' : 'text-gray-900 dark:text-gray-100'}`}>
                {children}
              </strong>
            ),
            em: ({ children }) => (
              <em className={`italic ${getTextColorClass()}`}>
                {children}
              </em>
            ),
            // Code
            code: ({ children, className }) => {
              const isInline = !className;
              return isInline ? (
                <code className={`bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm font-mono ${getTextColorClass()}`}>
                  {children}
                </code>
              ) : (
                <code className={className}>
                  {children}
                </code>
              );
            },
            pre: ({ children }) => (
              <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg overflow-x-auto mb-2">
                {children}
              </pre>
            ),
            // Blockquotes
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-blue-500 pl-4 my-2 italic text-gray-700 dark:text-gray-300">
                {children}
              </blockquote>
            ),
            // Links
            a: ({ children, href }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline font-medium transition-colors"
              >
                <span>{children}</span>
                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ),
            // Images - Let ReactMarkdown handle standard markdown images
            img: ({ src, alt }) => {
              // Don't render if src is empty or invalid
              if (!src || src.trim() === '') {
                return null;
              }
              
              return (
                <img 
                  src={src} 
                  alt={alt} 
                  className="max-h-[300px] w-auto object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => handleImageClick(src)}
                />
              );
            },
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>

      <ImageModal
        isOpen={isOpen}
        onClose={closeGallery}
        images={images}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
      />
    </>
  );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

// Custom comparison function for React.memo
const areEqual = (prevProps: MarkdownRendererProps, nextProps: MarkdownRendererProps) => {
  return prevProps.content === nextProps.content && prevProps.className === nextProps.className;
};

export default React.memo(MarkdownRenderer, areEqual);